import { Link } from "@tanstack/react-router";
import { PortalAccountMenu } from "./PortalAccountMenu";
import { WorkspacePage } from "./PortalAppShell";
import { ProblemRecordTable } from "./ProblemRecordTable";
import { InvestigationContext } from "./InvestigationContext";
import { formatRecordCost } from "@/lib/problem-record-presentation";
import { ArrowLeft, FolderOpen, ShieldCheck, Trophy } from "lucide-react";
import { PortalDataError, PortalLoadingState } from "./PortalDataState";
import { useLiveProblemRecordQuery, useLiveProblemRecordsQuery } from "@/lib/portal-queries";
import type { PortalProblemRecord } from "@/lib/portal-runtime";

const severityTone: Record<PortalProblemRecord["severity"], string> = {
  UNASSESSED: "border-border bg-muted text-muted-foreground",
  CRITICAL: "border-destructive/40 bg-destructive/10 text-destructive",
  HIGH: "border-warning/40 bg-warning/10 text-warning",
  MEDIUM: "border-primary/40 bg-primary/10 text-primary",
  LOW: "border-border bg-muted text-muted-foreground",
};

const statusTone: Record<PortalProblemRecord["status"], string> = {
  DRAFT: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  OPEN: "border-primary/50 bg-primary/15 text-primary",
  ON_HOLD: "border-warning/40 bg-warning/10 text-warning",
  CLOSED: "border-success/40 bg-success/10 text-success",
};

export function LiveProblemRecordRegistry({ embedded = false }: { embedded?: boolean }) {
  const records = useLiveProblemRecordsQuery();
  const content = records.isPending ? (
    <PortalLoadingState label="Loading live Problem Records" />
  ) : records.isError ? (
    <PortalDataError error={records.error} onRetry={() => void records.refetch()} />
  ) : records.data.length === 0 ? (
    <section className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
      <FolderOpen className="mx-auto size-7 text-muted-foreground" aria-hidden="true" />
      <h2 className="mt-3 font-semibold">No Problem Records in this Division</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The live read completed successfully. No demo records were substituted.
      </p>
    </section>
  ) : (
    <>
      <section className="grid gap-4 sm:grid-cols-3">
        <Summary label="Total records" value={String(records.data.length)} />
        <Summary
          label="Draft review"
          value={String(records.data.filter((record) => record.status === "DRAFT").length)}
        />
        <Summary
          label="Open"
          value={String(records.data.filter((record) => record.status === "OPEN").length)}
        />
      </section>
      <ProblemRecordTable records={records.data} />
    </>
  );
  return embedded ? (
    <div className="space-y-5">{content}</div>
  ) : (
    <WorkspacePage
      title="Problem Record Registry"
      subtitle="Source-backed review records for the selected Division"
    >
      {content}
    </WorkspacePage>
  );
}

export function LiveProblemRecordDetail({ id }: { id: string }) {
  const record = useLiveProblemRecordQuery(id);
  return (
    <ProblemRecordShell title="Problem Record" subtitle="Closed-loop quality case file">
      {record.isPending ? (
        <PortalLoadingState label="Loading live Problem Record" />
      ) : record.isError ? (
        <PortalDataError error={record.error} onRetry={() => void record.refetch()} />
      ) : (
        <>
          <section className="rounded-xl border border-border bg-card p-6">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded bg-primary/15 px-2 py-0.5 font-semibold text-primary">
                {record.data.displayId}
              </span>
              <span
                className={`rounded border px-2 py-0.5 font-medium ${severityTone[record.data.severity]}`}
              >
                {record.data.severity}
              </span>
              <span
                className={`rounded border px-2 py-0.5 font-medium ${statusTone[record.data.status]}`}
              >
                {record.data.status === "DRAFT"
                  ? "DRAFT REVIEW"
                  : record.data.status.replace("_", " ")}
              </span>
              <span className="rounded border border-border px-2 py-0.5 text-muted-foreground">
                Version {record.data.version}
              </span>
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight">{record.data.title}</h1>
            <p className="mt-3 max-w-4xl text-sm leading-6 text-muted-foreground">
              {record.data.summary || "No summary supplied."}
            </p>
            {record.data.classification === "HISTORICAL_REAL" && (
              <div className="mt-5 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm leading-6 text-foreground">
                <strong>Historical real-data review candidate.</strong> The claim pattern, linked
                claim count, and source-reported cost are real imported history. Severity, owner,
                root cause, corrective action, and current relevance have not been established.
              </div>
            )}
            <dl className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              {record.data.claimCount !== undefined && (
                <Detail label="Linked claims" value={record.data.claimCount.toLocaleString()} />
              )}
              {record.data.impact && (
                <Detail label="Source-reported cost" value={formatRecordCost(record.data)} />
              )}
              {record.data.dataCutoffAt && (
                <Detail
                  label="Historical data through"
                  value={new Date(record.data.dataCutoffAt).toLocaleDateString()}
                />
              )}
              {record.data.reconciliation && (
                <Detail label="Reconciliation" value={record.data.reconciliation} />
              )}
              <Detail label="Owner" value={record.data.ownerUserId ?? "Unassigned"} />
              <Detail label="Created" value={new Date(record.data.createdAt).toLocaleString()} />
              <Detail
                label="Last updated"
                value={new Date(record.data.updatedAt).toLocaleString()}
              />
              <Detail label="Updated by" value={record.data.updatedBy} />
            </dl>
          </section>
          <InvestigationContext key={record.data.id} record={record.data} />
        </>
      )}
    </ProblemRecordShell>
  );
}

function ProblemRecordShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <FolderOpen className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold">{title}</div>
              <div className="text-xs text-muted-foreground">{subtitle}</div>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Link>
            <Link
              to="/top5"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <Trophy className="h-3.5 w-3.5" /> Top 5
            </Link>
            <Link
              to="/problem-records"
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Registry
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1500px] space-y-6 px-6 py-8">{children}</main>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-all font-medium">{value}</dd>
    </div>
  );
}
